import {
  Component, ChangeDetectionStrategy, ChangeDetectorRef,
  OnInit, OnDestroy, ViewChild, ElementRef, NgZone,
} from '@angular/core';
import {
  Router, RouterOutlet,
  NavigationStart, NavigationEnd, NavigationCancel, NavigationError,
} from '@angular/router';
import { NavbarComponent } from '../navbar/navbar.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'hp-main-layout',
  standalone: true,
  imports: [RouterOutlet, NavbarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <hp-navbar></hp-navbar>

    <!-- Top navigation progress bar -->
    <div
      class="hp-nav-bar"
      [class.hp-nav-bar--loading]="navLoading"
      [class.hp-nav-bar--done]="navDone"
    ></div>

    <main class="mt-[60px] min-h-[calc(100vh-60px)] overflow-x-hidden">
      <router-outlet #outletEl (activate)="onRouteActivate()"></router-outlet>
    </main>
  `,
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  @ViewChild('outletEl', { read: ElementRef }) outletRef!: ElementRef<HTMLElement>;

  navLoading = false;
  navDone    = false;

  private sub!: Subscription;
  private doneTimer: ReturnType<typeof setTimeout> | null = null;
  private raf1 = 0;
  private raf2 = 0;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.sub = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // Cancel any in-flight slide animation from a previous navigation
        cancelAnimationFrame(this.raf1);
        cancelAnimationFrame(this.raf2);
        if (this.doneTimer) clearTimeout(this.doneTimer);
        this.navLoading = true;
        this.navDone    = false;
        this.cdr.markForCheck();
      } else if (
        event instanceof NavigationEnd    ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.navLoading = false;
        this.navDone    = true;
        this.cdr.markForCheck();
        this.doneTimer = setTimeout(() => {
          this.navDone = false;
          this.cdr.markForCheck();
        }, 500);
      }
    });
  }

  onRouteActivate() {
    const outlet = this.outletRef?.nativeElement;
    if (!outlet) return;

    const page = outlet.nextElementSibling as HTMLElement | null;
    if (!page) return;

    // Synchronously place the page off-screen so the browser never paints it
    // at position 0 before the animation starts.
    page.style.transition = 'none';
    page.style.transform  = 'translateX(100%)';
    page.style.opacity    = '0';
    page.style.willChange = 'transform, opacity';

    // Run rAFs outside Angular's zone — these are pure DOM operations that
    // don't need to trigger change detection.
    this.ngZone.runOutsideAngular(() => {
      // rAF 1: browser commits the initial styles above.
      // rAF 2: browser is ready to paint — start the transition.
      this.raf1 = requestAnimationFrame(() => {
        this.raf2 = requestAnimationFrame(() => {
          page.style.transition = 'transform 340ms cubic-bezier(0.22, 1, 0.36, 1), opacity 260ms ease';
          page.style.transform  = 'translateX(0)';
          page.style.opacity    = '1';

          // Once the slide finishes, remove all inline styles so they don't
          // interfere with the component's own CSS transitions.
          page.addEventListener('transitionend', () => {
            page.style.transition = '';
            page.style.transform  = '';
            page.style.opacity    = '';
            page.style.willChange = '';
          }, { once: true });
        });
      });
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    if (this.doneTimer) clearTimeout(this.doneTimer);
    cancelAnimationFrame(this.raf1);
    cancelAnimationFrame(this.raf2);
  }
}
